import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { travelRequestsApi } from '../../api/travel';
import { ApiError } from '../../api/client';
import { PageHeader } from '../../components/PageHeader';
import { SectionHeader } from '../../components/SectionHeader';
import { DataTable } from '../../components/DataTable';
import { StatusBadge } from '../../components/StatusBadge';
import { useToast } from '../../components/Toast';
import { EmployeePicker } from '../../components/EmployeePicker';

export const TRAVEL_STATUS_LABELS: Record<string, string> = {
  draft: 'Draft', submitted: 'Submitted', under_review: 'Under Review', returned: 'Returned for Redraft',
  clarification_required: 'Additional Information Required', approved: 'Approved', rejected: 'Rejected',
  cancelled: 'Cancelled', in_progress: 'In Progress', completed: 'Completed', closed: 'Closed', archived: 'Archived',
};
const TRAVEL_TYPE_LABELS: Record<string, string> = {
  domestic: 'Domestic', international: 'International', field_mission: 'Field Mission', training: 'Training',
  workshop: 'Workshop', conference: 'Conference', partner_visit: 'Partner Visit', monitoring_visit: 'Monitoring Visit',
  emergency_deployment: 'Emergency Deployment', other: 'Other',
};

interface CreateTravelForm {
  travellerEmployeeId: number; travelType: string; purpose: string; origin: string; destination: string;
  departureDate: string; returnDate: string; modeOfTravel?: string;
}

export function TravelRequestsListPage() {
  const [statusFilter, setStatusFilter] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();
  const { register, handleSubmit, reset } = useForm<CreateTravelForm>();

  const { data, isLoading } = useQuery({
    queryKey: ['travel-requests', statusFilter],
    queryFn: () => travelRequestsApi.list({ status: statusFilter || undefined }),
  });

  const createMutation = useMutation({
    mutationFn: (input: CreateTravelForm) => travelRequestsApi.create({ ...input }),
    onSuccess: (result) => {
      showSuccess(`Travel request ${result.data.requestReference} created.`);
      queryClient.invalidateQueries({ queryKey: ['travel-requests'] });
      setShowCreateForm(false);
      reset();
      navigate(`/travel/${result.data.id}`);
    },
    onError: (err: ApiError) => showError(err.message),
  });

  const requests = data?.data ?? [];

  return (
    <div className="page">
      <PageHeader title="Travel Requests" description="Request, review, and authorize staff travel." />

      <SectionHeader title="Requests" actions={
        <>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="">All statuses</option>
            {Object.entries(TRAVEL_STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <button onClick={() => setShowCreateForm((s) => !s)}>{showCreateForm ? 'Cancel' : 'New Travel Request'}</button>
        </>
      } />

      {showCreateForm && (
        <form className="inline-form" onSubmit={handleSubmit((d) => createMutation.mutate(d))}>
          <label>Traveller <EmployeePicker {...register('travellerEmployeeId', { required: true, valueAsNumber: true })} /></label>
          <label>Travel Type
            <select {...register('travelType', { required: true })}>
              {Object.entries(TRAVEL_TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </label>
          <label>Purpose <input {...register('purpose', { required: true })} /></label>
          <label>Origin <input {...register('origin', { required: true })} /></label>
          <label>Destination <input {...register('destination', { required: true })} /></label>
          <label>Departure Date <input type="date" {...register('departureDate', { required: true })} /></label>
          <label>Return Date <input type="date" {...register('returnDate', { required: true })} /></label>
          <label>Mode of Travel
            <select {...register('modeOfTravel')}>
              <option value="">-- Not yet decided --</option>
              <option value="air">Air</option>
              <option value="road_organization_vehicle">Organization Vehicle</option>
              <option value="road_hired_vehicle">Hired Vehicle</option>
              <option value="road_public_transport">Public Transport</option>
              <option value="train">Train</option>
              <option value="other">Other</option>
            </select>
          </label>
          <button type="submit" disabled={createMutation.isPending}>Create Draft</button>
        </form>
      )}

      {isLoading ? <p className="muted">Loading…</p> : (
        <DataTable
          rows={requests}
          rowKey={(r) => r.id}
          columns={[
            { header: 'Reference', render: (r) => <a onClick={() => navigate(`/travel/${r.id}`)} className="table-link">{r.request_reference}</a> },
            { header: 'Type', render: (r) => TRAVEL_TYPE_LABELS[r.travel_type] ?? r.travel_type },
            { header: 'Route', render: (r) => `${r.origin} → ${r.destination}` },
            { header: 'Departure', render: (r) => r.departure_date },
            { header: 'Return', render: (r) => r.return_date },
            { header: 'Status', render: (r) => <StatusBadge status={r.status} label={TRAVEL_STATUS_LABELS[r.status]} /> },
          ]}
        />
      )}
    </div>
  );
}
